import React, { useState, useEffect, useMemo } from "react";
import Slider from "react-slick";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons";
import { ReactComponent as PriceIcon } from "feather-icons/dist/icons/award.svg";
import { ReactComponent as LocationIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as TimeIcon } from "feather-icons/dist/icons/bookmark.svg";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import ItineraryCard from "components/destination-cards/ItineraryCard";


const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV



const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-8 lg:py-8`;

const HeadingWithControl = tw.div`flex flex-col items-center sm:items-stretch sm:flex-row justify-between`;
const Heading = tw(SectionHeading)``;
const Controls = tw.div`flex items-center`;
const ControlButton = styled(PrimaryButtonBase)`
  ${tw`mt-4 sm:mt-0 first:ml-0 ml-6 rounded-full p-2`}
  svg {
    ${tw`w-6 h-6`}
  }
`;
const PrevButton = tw(ControlButton)`bg-[#0049AC]`;
const NextButton = tw(ControlButton)`bg-[#0049AC]`;


const CardSlider = styled(Slider)`
  ${tw`mt-8`}
  .slick-track { 
    ${tw`flex`}
  }
  .slick-slide {
    ${tw`h-auto flex justify-center mb-1`}
  }
`;

const Card = tw.div`
  flex flex-col max-w-sm 
  sm:border sm:rounded-tl-4xl sm:rounded-br-5xl sm:rounded-bl-4xl 
  relative focus:outline-none border-solid border-gray-300
`;



const CardImage = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`w-full h-40 sm:h-48 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl`
]);

const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const TitleContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center`;
const TitleCard = tw.h5`text-xl font-bold`;

const DurationInfo = styled.div`
  ${tw`flex items-center sm:ml-4 mt-2 sm:mt-0`}
  svg {
    ${tw`w-6 h-6 text-yellow-500 fill-current`}
  }
`;
const TimeSpent = tw.span`ml-2 font-bold`;

const Description = tw.p`text-sm leading-loose mt-2 sm:mt-4`;

const SecondaryInfoContainer = tw.div`flex flex-col sm:flex-row mt-2 sm:mt-4`;
const IconWithText = tw.div`flex items-center mr-6 my-2 sm:my-0`;
const IconContainer = styled.div`
  ${tw`inline-block rounded-full p-2 bg-gray-700 text-gray-100`}
  svg {
    ${tw`w-3 h-3`}
  }
`;
const Text = tw.div`ml-2 text-sm font-semibold text-gray-800`;

const PrimaryButton = tw(PrimaryButtonBase)`mt-auto sm:text-lg rounded-none w-full rounded sm:rounded-none sm:rounded-br-4xl py-3 sm:py-6`;

const PrimaryButtonContainer = tw.div`flex w-full border-t border-gray-300`;
const PrimaryButtonHalf = tw(PrimaryButtonBase)`w-1/2 sm:py-4`;
const PrimaryButtonLeft = styled(PrimaryButtonHalf)`
  ${tw`rounded-bl-4xl border-t border-r border-gray-500`}
  border-right: 1px solid #6b7280 !important;
`;

const PrimaryButtonRight = tw(PrimaryButtonHalf)`rounded-br-4xl`;

const CloseButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      position: "absolute",
      top: "2%",  // Adjust as needed
      right: "4%", // Position at the end of the modal
      border: "none",
      fontSize: "30px",
      cursor: "pointer",
      zIndex: "50",
      color: "#333",
      background: "transparent",
    }}
    aria-label="Close"
  >
    &times;
  </button>
);

const Modal = tw.div`fixed z-20 inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  background: white;
  padding: 5px;
  border-radius: 8px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 90%; /* Restrict height */
`;

const Title = styled.h2`
  position: sticky;
  top: 0;
  background: white;
  text-align: center;
  width: 100%;
  padding: 15px 0;
  font-size: 24px;
  font-weight: bold;
  z-index: 10;
`;


const DownloadButton = styled.a`
  position: absolute;
  bottom: 10px;
  z-index:50;
  left: 20%;
  transform: translateX(-50%);
  width: 30%;
  background: #0049AC;
  color: white;
  text-align: center;
  padding: 10px;
  border-radius: 5px;
  text-decoration: none;
  cursor: pointer;
  font-weight: bold;
`;

const ScrollableContent = styled.div`
  overflow-y: auto;
  max-height: 100vh; /* Allow scrolling within modal */
  padding: 10px;
  padding-bottom:80px;
`;

export default () => {
  const [sliderRef, setSliderRef] = useState(null);
  const [cards, setCards] = useState([]);
  const [mobileNo, setMobileNo] = useState("6395977935"); // Default mobile number
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itineraryUrl, setItineraryUrl] = useState(null);
  const [destTourId, setDestTourId] = useState(null);

  // Fetch mobile number from the API or fallback to the default
  useEffect(() => {

    const fetchWhatsapp = async () => {
      try {

        const response = await fetch(`${API_URL}/web/home/wpnumber?title=whatsappNumber`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        }); // API endpoint updated
        const result = await response.json();

        if (result.header.resCode === 200 && result.data.length > 0) {
          const numberData = result.data.find((item) => item.title === "whatsappNumber"); // Find the correct video by title
          if (numberData) {
            setMobileNo(numberData.content)
          }
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    const fetchTours = async () => {
      try {

        const response = await fetch(`${API_URL}/web/home/trending-tour`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
          }
        }); // API endpoint updated
        const result = await response.json();

        if (result.header.resCode === 200 && result.data.length > 0) {
          const tourData = result.data
          setCards(tourData)
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };


    fetchWhatsapp();
    fetchTours();

  }, []);

  const slidesToShow = useMemo(() => (cards.length > 2 ? 3 : 1), [cards]);

  const sliderSettings = {
    arrows: false,
    slidesToShow: slidesToShow,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 1,
        }
      },
    ]
  };

  // Function to handle WhatsApp enquiry
  const handleEnquiry = (tour) => {
    const message = `Hello! I'm interested in the tour package "${tour.title}".\n\nDetails:\nLocation: ${tour.location}\nPrice: ${tour.price}\nDuration: ${tour.duration_days}\nDescription: ${tour.description}\n\nPlease provide more information.`;
    const whatsappLink = `https://wa.me/${mobileNo}?text=${encodeURIComponent(message)}`;
    window.open(whatsappLink, "_blank");
  };

  // Dummy data to be used if API fails
  const dummyData = [
    {
      id: 1,
      title: "Vrindavan Bhakti",
      location: "Mathura, Uttar Pradesh",
      destination_id: 101,
      price: "₹ 5000",
      duration_days: "4D/3N",
      description:
        "Mathura, the birthplace of Lord Krishna, is a city rich in religious and cultural heritage. Key attractions include the Sri Krishna Janmabhoomi Temple, marking Krishna's birthplace, Vishram Ghat along the Yamuna River, and Govardhan Hill, linked to Krishna's legendary story.",
      created_at: "2024-01-01",
      updated_at: "2024-01-10",
      thumbnail:
        "https://images.pexels.com/photos/18562891/pexels-photo-18562891/free-photo-of-woman-standing-near-fishermen-on-galata-bridge.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
      flag: true,
    },
  ];

  const openItinerary = (tour) => {
    document.body.style.overflow = 'hidden';
    setIsModalOpen(true);
    setDestTourId(tour.id);
    setItineraryUrl(tour.itinerary_url);
  };

  const handleClose = () => {
    document.body.style.overflow = 'auto';
    setIsModalOpen(false)
    // setHeadStick(null);
  };


  return (
    <>
      {cards.length > 0 &&
        <Container>
          <Content>
            <HeadingWithControl>
              <Heading>Trending Tour Packages</Heading>
              <Controls>
                <PrevButton onClick={sliderRef?.slickPrev}><ChevronLeftIcon /></PrevButton>
                <NextButton onClick={sliderRef?.slickNext}><ChevronRightIcon /></NextButton>
              </Controls>
            </HeadingWithControl>
            <CardSlider ref={setSliderRef} {...sliderSettings}>
              {cards.map((card, index) => (
                <Card key={index}>
                  <CardImage imageSrc={card.thumbnail} />
                  <TextInfo>
                    <TitleContainer>
                      <TitleCard>{card.title}</TitleCard>
                      <DurationInfo>
                        <TimeIcon />
                        <TimeSpent>{card.duration_days}</TimeSpent>
                      </DurationInfo>
                    </TitleContainer>
                    <SecondaryInfoContainer>
                      <IconWithText>
                        <IconContainer>
                          <LocationIcon />
                        </IconContainer>
                        <Text>{card.location}</Text>
                      </IconWithText>
                      <IconWithText>
                        <IconContainer>
                          <PriceIcon />
                        </IconContainer>
                        <Text>{"₹ " + parseInt(card.price)}</Text>
                      </IconWithText>
                    </SecondaryInfoContainer>
                    <Description>{card.description.split(/\s+/).slice(0, 20).join(" ") + (card.description.split(/\s+/).length > 20 ? "..." : "")}</Description>
                  </TextInfo>
                  <PrimaryButtonContainer>
                    <PrimaryButtonLeft onClick={() => handleEnquiry(card)}>Make Enquiry</PrimaryButtonLeft>
                    <PrimaryButtonRight onClick={() => openItinerary(card)}>Show Itinerary</PrimaryButtonRight>
                  </PrimaryButtonContainer>
                </Card>
              ))}
            </CardSlider>
          </Content>
        </Container>
      }
      {isModalOpen && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={handleClose} />
            <Title>Turning Plans into Experiences @ Traviator</Title>
            <DownloadButton onClick={() => window.open(itineraryUrl, "_blank")}>
              Download Itinerary
            </DownloadButton>
            {/* This wrapper makes only ItineraryCard scrollable */}
            <ScrollableContent>
              <ItineraryCard tourId={destTourId} />
            </ScrollableContent>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
